import { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, SearchCheck, Handshake, FileSignature, CalendarDays, ListChecks, FolderLock,
  Scale, Euro, BarChart3, KeyRound, Upload, BellRing, Sparkles, Radar, FileBarChart, Database, UserCog, ShieldCheck, ScrollText, DatabaseBackup,
  LogOut, Menu, ExternalLink, UserCircle2,
} from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { LOGO_URL } from "@/lib/brand";
import { useGAuth } from "../GAuth";
import { GlobalSearch, QuickAdd, NotificationsBell } from "./TopBarTools";

const NAV = [
  { group: "Lavoro", items: [
    { to: "/gestionale", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/gestionale/proprietari", label: "PROPRIETARI DA ACQUISIRE", icon: Handshake, res: "acquisizioni", evidenza: true },
    { to: "/gestionale/acquirenti", label: "ACQUIRENTI E RICHIESTE", icon: SearchCheck, res: "richieste", evidenza: true },
    { to: "/gestionale/annunci", label: "RICERCA ANNUNCI PRIVATI", icon: Radar, res: "annunci", evidenza: true },
    { to: "/gestionale/valutazioni", label: "VALUTAZIONI E BROCHURE", icon: FileBarChart, res: "valutazioni", evidenza: true },
    { to: "/gestionale/fonti-dati", label: "Fonti dati", icon: Database, res: "annunci" },
    { to: "/gestionale/contatti", label: "Contatti", icon: Users, res: "contatti" },
    { to: "/gestionale/immobili", label: "Immobili", icon: Building2, res: "immobili" },
    { to: "/gestionale/incarichi", label: "Incarichi", icon: FileSignature, res: "incarichi" },
    { to: "/gestionale/agenda", label: "Agenda e visite", icon: CalendarDays, res: "agenda" },
    { to: "/gestionale/attivita", label: "Attività", icon: ListChecks, res: "attivita" },
    { to: "/gestionale/documenti", label: "Documenti", icon: FolderLock, res: "documenti" },
    { to: "/gestionale/trattative", label: "Trattative", icon: Scale, res: "trattative" },
    { to: "/gestionale/provvigioni", label: "Provvigioni", icon: Euro, res: "provvigioni" },
    { to: "/gestionale/report", label: "Report", icon: BarChart3, res: "report" },
    { to: "/gestionale/locazioni", label: "Locazioni", icon: KeyRound, res: "locazioni" },
    { to: "/gestionale/avvisi", label: "Avvisi", icon: BellRing },
    { to: "/gestionale/importazioni", label: "Importazioni", icon: Upload, res: "importazioni" },
    { to: "/gestionale/assistente-ai", label: "Assistente AI", icon: Sparkles },
  ] },
  { group: "Amministrazione", items: [
    { to: "/gestionale/utenti", label: "Utenti e inviti", icon: UserCog, res: "utenti" },
    { to: "/gestionale/permessi", label: "Ruoli e permessi", icon: ShieldCheck, res: "impostazioni" },
    { to: "/gestionale/registro", label: "Registro attività", icon: ScrollText, res: "registro" },
    { to: "/gestionale/sistema", label: "Sistema e backup", icon: DatabaseBackup, titolare: true },
  ] },
];

function NavItems({ onNavigate }) {
  const { can, user } = useGAuth();
  return NAV.map((g) => {
    const items = g.items.filter((i) => (i.titolare ? user.ruolo === "titolare" : !i.res || can(i.res)));
    if (!items.length) return null;
    return (
      <div key={g.group} className="mb-6">
        <p className="px-4 mb-2 text-[10px] uppercase tracking-[0.25em] text-white/30">{g.group}</p>
        {items.map((i) => i.to ? (
          <NavLink key={i.label} to={i.to} end={i.end} onClick={onNavigate}
            className={({ isActive }) => `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors ${i.evidenza ? "font-bold text-[12.5px] tracking-wide border border-white/20 my-1 " : ""}${isActive ? "bg-brand text-white" : "text-white/70 hover:bg-white/[0.06] hover:text-white"}`}
            data-testid={`gnav-${i.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <i.icon className="h-4 w-4" /> {i.label}
          </NavLink>
        ) : (
          <div key={i.label} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/30 cursor-not-allowed" title={`Disponibile con la Fase ${i.fase}`}>
            <i.icon className="h-4 w-4" /> <span className="flex-1">{i.label}</span>
            <span className="text-[10px] rounded-full border border-white/10 px-2 py-0.5">Fase {i.fase}</span>
          </div>
        ))}
      </div>
    );
  });
}

function Sidebar({ onNavigate }) {
  const { user, logout } = useGAuth();
  const navigate = useNavigate();
  return (
    <div className="flex h-full flex-col bg-[#121412] text-white">
      <div className="px-6 pt-7 pb-6">
        <img src={LOGO_URL} alt="Casamore" className="h-16 w-auto -ml-2 brightness-0 invert" />
        <p className="text-[10px] text-white/35 mt-2 uppercase tracking-[0.3em]">Gestionale</p>
      </div>
      <nav className="flex-1 overflow-y-auto px-3">{<NavItems onNavigate={onNavigate} />}</nav>
      <div className="p-3 border-t border-white/10">
        <NavLink to="/gestionale/profilo" onClick={onNavigate} className="flex items-center gap-3 rounded-xl px-4 py-2.5 hover:bg-white/[0.06]" data-testid="gnav-profilo">
          <UserCircle2 className="h-8 w-8 text-white/50" />
          <span className="min-w-0"><span className="block text-sm truncate">{user.nome}</span><span className="block text-xs text-white/40 capitalize">{user.ruolo}</span></span>
        </NavLink>
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl px-4 py-2 text-sm text-white/60 hover:bg-white/[0.06]"><ExternalLink className="h-4 w-4" /> Sito pubblico</a>
        <button onClick={async () => { await logout(); navigate("/gestionale/accesso"); }} className="w-full flex items-center gap-3 rounded-xl px-4 py-2 text-sm text-white/60 hover:bg-white/[0.06]" data-testid="g-logout"><LogOut className="h-4 w-4" /> Esci</button>
      </div>
    </div>
  );
}

function TopBar({ onMenu, ambiente }) {
  return (
    <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-border-subtle bg-warm/85 backdrop-blur-xl px-4 md:px-8 h-16">
      <button onClick={onMenu} className="md:hidden grid place-items-center h-11 w-11 rounded-xl border border-border-subtle bg-white" aria-label="Apri menu" data-testid="g-mobile-menu"><Menu className="h-5 w-5" /></button>
      <GlobalSearch />
      {ambiente === "prova" && <span className="hidden sm:inline rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-3 py-1" data-testid="g-env-badge">Ambiente di prova</span>}
      <NotificationsBell />
      <QuickAdd />
    </header>
  );
}

export default function GLayout() {
  const { session } = useGAuth();
  const [open, setOpen] = useState(false);
  if (session === null) return <div className="min-h-screen grid place-items-center bg-warm text-anthracite/50" data-testid="g-loading">Caricamento…</div>;
  if (!session) return <Navigate to="/gestionale/accesso" replace />;
  return (
    <div className="min-h-screen bg-warm text-anthracite">
      <aside className="hidden md:block fixed inset-y-0 left-0 w-72"><Sidebar /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-80 border-0 bg-[#121412]">
          <SheetTitle className="sr-only">Menu gestionale</SheetTitle>
          <Sidebar onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="md:ml-72">
        <TopBar onMenu={() => setOpen(true)} ambiente={session.ambiente} />
        <main className="px-4 md:px-8 py-8 max-w-7xl"><Outlet /></main>
      </div>
    </div>
  );
}
